import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  PencilIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  ChartBarIcon,
  CalendarIcon,
  ExclamationTriangleIcon,
  ArrowLeftIcon,
  ServerIcon,
  CloudIcon,
  ShieldCheckIcon,
  MagnifyingGlassIcon,
  WrenchScrewdriverIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import api from '../../services/api';
import { PermissionGate, useDynamicPermissions } from '../../hooks/useDynamicPermissions';
import ResourceSyncModal from '../../components/ResourceSyncModal';
import ResourceDetailModal from '../../components/ResourceDetailModal';
import SlaPolicies from './SlaPolicies';
import { getSyncRecencyStatus } from '../../utils/dateHelpers';
import { useDateFormatter } from '../../hooks/useDateFormatter';
import PatchingServicesDetail, {
  EngagementPatchingService
} from '../../components/engagements/PatchingServicesDetail';
import ServiceProvidersTab from './ServiceProvidersTab';
import EngagementMiscTab from '../../components/engagements/EngagementMiscTab';

interface Agent {
  id: string;
  userId: string;
  role: string;
  hourlyRate: number;
  allocatedHours: number | null;
  utilizedHours: number;
  startDate: string;
  endDate: string | null;
  isActive: boolean;
  User: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  Group: {
    id: string;
    name: string;
  };
}

interface PlanAssignment {
  id: string;
  startDate: string;
  endDate: string | null;
  actualHours: number;
  status: string;
  customizations: any;
  ServicePlan: {
    id: string;
    name: string;
    description: string;
    type: string;
    environmentType: string;
    estimatedHours: number | null;
    basePrice: number | null;
    Offer?: {
      id: string;
      name: string;
      offerType: string;
    };
  };
}

interface WorkNote {
  id: string;
  note: string;
  isPublic: boolean;
  timestamp: string;
  User: {
    id: string;
    firstName: string;
    lastName: string;
  };
}

interface Engagement {
  id: string;
  number: string;
  name: string;
  cloudProvider: string;
  description: string | null;
  status: string;
  billingType: string;
  currency: string;
  startDate: string;
  endDate: string | null;
  totalBudget: number | null;
  usedBudget: number;
  contractValue: number | null;
  minimumServiceFee: number;
  platformCost: number;
  billable?: boolean;
  isServiceCreditEngagement?: boolean;
  complimentaryHours?: number | null;
  Customer: {
    id: string;
    accountName: string;
    accountShortName: string;
    emailAddress: string;
  };
  createdBy: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  EngagementAgent: Agent[];
  ServicePlanAssignment: PlanAssignment[];
  EngagementWorkNote: WorkNote[];
  patchingServices?: EngagementPatchingService[];
  createdAt: string;
  updatedAt: string;
}

interface BillingSummary {
  engagement: {
    id: string;
    name: string;
    totalBudget: number | null;
    usedBudget: number;
    currency: string;
  };
  summary: {
    totalProjectedCost: number;
    totalActualCost: number;
    totalAllocatedHours: number;
    totalUtilizedHours: number;
    budgetUtilization: number;
  };
  agents: Array<{
    agentId: string;
    agentName: string;
    role: string;
    hourlyRate: number;
    allocatedHours: number;
    utilizedHours: number;
    projectedCost: number;
    actualCost: number;
  }>;
}

const EngagementDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [engagement, setEngagement] = useState<Engagement | null>(null);
  const [billingSummary, setBillingSummary] = useState<BillingSummary | null>(null);
  const [resources, setResources] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [resourcesLoading, setResourcesLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showResourceSyncModal, setShowResourceSyncModal] = useState(false);
  const [showResourceDetailModal, setShowResourceDetailModal] = useState(false);
  const [selectedResourceId, setSelectedResourceId] = useState<string | null>(null);
  const [resourceSearchTerm, setResourceSearchTerm] = useState('');
  const { formatDate, formatDateTime } = useDateFormatter();
  const navigate = useNavigate();

  const { can, loading: permissionsLoading } = useDynamicPermissions();

  // Check permissions for engagements using can function directly
  const canView = can('engagements', 'read');

  useEffect(() => {
    // Don't check permissions if they're still loading
    if (permissionsLoading) {
      return;
    }

    if (!canView) {
      navigate('/dashboard');
      return;
    }
  }, [canView, navigate, permissionsLoading]);

  const fetchEngagement = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/engagements/${id}`);
      setEngagement(response.data);
      setError(null);
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to fetch engagement';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchBillingSummary = useCallback(async () => {
    try {
      const response = await api.get(`/engagements/${id}/billing-summary`);
      setBillingSummary(response.data);
    } catch (error: any) {
      console.error('Error fetching billing summary:', error);
    }
  }, [id]);

  const fetchResources = useCallback(async () => {
    try {
      setResourcesLoading(true);
      const response = await api.get(`/engagements/${id}/resources`);
      setResources(response.data.resources || []);
    } catch (error: any) {
      console.error('Error fetching resources:', error);
      // Only show toast for actual errors, not rate limiting
      if (error.response?.status !== 429) {
        toast.error('Failed to load resources');
      }
    } finally {
      setResourcesLoading(false);
    }
  }, [id]);

  useEffect(() => {
    // Only fetch data if we have permission and an ID
    if (!permissionsLoading && canView && id) {
      fetchEngagement();
      fetchBillingSummary();
      // Only fetch resources when the tab is active
      if (activeTab === 'resources') {
        fetchResources();
      }
    }
  }, [id, permissionsLoading, canView, activeTab, fetchEngagement, fetchBillingSummary, fetchResources]);

  const formatCurrency = (amount: number | null, currency: string = 'USD') => {
    if (amount === null) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const formatDateShort = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return formatDate(dateString);
  };

  const formatDateTimeShort = (dateString: string) => {
    return formatDateTime(dateString);
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'on_hold':
        return 'bg-yellow-100 text-yellow-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const tabs = [
    { id: 'overview', name: 'Overview', icon: DocumentTextIcon },
    { id: 'agents', name: 'Agents', icon: UserGroupIcon },
    { id: 'plans', name: 'Service Plans', icon: ChartBarIcon },
    { id: 'sla', name: 'SLA Policies', icon: ShieldCheckIcon },
    { id: 'serviceproviders', name: 'Service Providers', icon: CloudIcon },
    { id: 'billing', name: 'Billing', icon: CurrencyDollarIcon },
    { id: 'notes', name: 'Work Notes', icon: DocumentTextIcon },
    { id: 'misc', name: 'Misc', icon: WrenchScrewdriverIcon }
  ];

  const renderOverview = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {/* Basic Info */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <DocumentTextIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Status</dt>
                  <dd className="flex items-baseline">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeColor(engagement?.status || '')}`}>
                      {engagement?.status.replace('_', ' ')}
                    </span>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Budget Info */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CurrencyDollarIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Budget Utilization</dt>
                  <dd className="flex items-baseline">
                    <div className="text-lg font-medium text-gray-900">
                      {billingSummary?.summary.budgetUtilization.toFixed(1)}%
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Timeline */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CalendarIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Duration</dt>
                  <dd className="flex items-baseline">
                    <div className="text-sm text-gray-900">
                      {formatDateShort(engagement?.startDate || null)} - {formatDateShort(engagement?.endDate || null)}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {engagement?.isServiceCreditEngagement && (
        <div className="w-full flex items-center rounded-md border border-blue-200 bg-blue-50 px-3 py-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-blue-700">
            Service Credit Engagement
          </span>
          <div className="ml-auto flex items-center gap-3">            
            <span className="text-xs text-blue-700 uppercase tracking-wide">Complimentary Hours</span>
            <span className="text-sm font-semibold text-blue-900">{engagement.complimentaryHours ?? 0}</span>
          </div>
        </div>
      )}

      {engagement?.patchingServices && engagement.patchingServices.length > 0 && (
        <PatchingServicesDetail
          patchingServices={engagement.patchingServices}
          engagementStartDate={engagement.startDate}
          currency={engagement.currency}
        />
      )}

      {/* Detailed Information */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Engagement Details</h3>
        </div>
        <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
          <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
            <div>
              <dt className="text-sm font-medium text-gray-500">Engagement Name</dt>
              <dd className="mt-1 text-sm text-gray-900">{engagement?.name}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Cloud Provider</dt>
              <dd className="mt-1 text-sm text-gray-900">{engagement?.cloudProvider}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Billing Type</dt>
              <dd className="mt-1 text-sm text-gray-900">{engagement?.billingType.replace('_', ' ')}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Currency</dt>
              <dd className="mt-1 text-sm text-gray-900">{engagement?.currency}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Start Date</dt>
              <dd className="mt-1 text-sm text-gray-900">{formatDateShort(engagement?.startDate || null)}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">End Date</dt>
              <dd className="mt-1 text-sm text-gray-900">{formatDateShort(engagement?.endDate || null)}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Customer</dt>
              <dd className="mt-1 text-sm text-gray-900">{engagement?.Customer?.accountName || 'N/A'}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Minimum Service Fee</dt>
              <dd className="mt-1 text-sm text-gray-900">{formatCurrency(engagement?.minimumServiceFee || 0, engagement?.currency)}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Platform Cost</dt>
              <dd className="mt-1 text-sm text-gray-900">{formatCurrency(engagement?.platformCost || 0, engagement?.currency)}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Billable</dt>
              <dd className="mt-1 text-sm text-gray-900">{engagement?.billable === false ? 'No' : 'Yes'}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Engagement Type</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {engagement?.isServiceCreditEngagement ? 'Service Credit Engagement' : 'Standard Engagement'}
              </dd>
            </div>
            {engagement?.isServiceCreditEngagement && (
              <div>
                <dt className="text-sm font-medium text-gray-500">Configured Complimentary Hours</dt>
                <dd className="mt-1 text-sm text-gray-900">{engagement?.complimentaryHours ?? 0}</dd>
              </div>
            )}
            <div className="sm:col-span-2">
              <dt className="text-sm font-medium text-gray-500">Description</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {engagement?.description || 'No description provided'}
              </dd>
            </div>
          </dl>
        </div>
      </div>
    </div>
  );

  const renderAgents = () => (
    <div className="space-y-6">
      {engagement?.EngagementAgent && engagement.EngagementAgent.length > 0 ? (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Agent
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Group
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Hourly Rate
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Hours
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {engagement.EngagementAgent.map((agent) => (
                  <tr key={agent.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {agent.User.firstName} {agent.User.lastName}
                          </div>
                          <div className="text-sm text-gray-500">{agent.User.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                        {agent.role.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {agent.Group.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(agent.hourlyRate, engagement?.currency)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div>
                        Allocated: {agent.allocatedHours || 'N/A'}
                      </div>
                      <div className="text-gray-500">
                        Utilized: {agent.utilizedHours}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        agent.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {agent.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="text-center py-12">
          <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No agents assigned</h3>
          <p className="mt-1 text-sm text-gray-500">
            Agents will appear here once groups are assigned to this engagement.
          </p>
        </div>
      )}
    </div>
  );

  const renderPlans = () => (
    <div className="space-y-6">
      {engagement?.ServicePlanAssignment && engagement.ServicePlanAssignment.length > 0 ? (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Offer</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Environment</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Timeline</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hours</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Base Price</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {engagement.ServicePlanAssignment.map((assignment) => (
                  <tr key={assignment.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{assignment.ServicePlan.name}</div>
                      {assignment.ServicePlan.description && (
                        <div className="text-sm text-gray-500 max-w-xs truncate">{assignment.ServicePlan.description}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex px-2 py-1 text-xs font-medium rounded-md bg-blue-50 text-blue-700">
                        {assignment.ServicePlan.type?.replace('_', ' ') || 'N/A'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{assignment.ServicePlan.Offer?.name || 'N/A'}</div>
                      <div className="text-xs text-gray-500">{assignment.ServicePlan.Offer?.offerType?.replace('_', ' ') || ''}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {assignment.ServicePlan.environmentType}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDateShort(assignment.startDate)} - {formatDateShort(assignment.endDate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div>Estimated: {assignment.ServicePlan.estimatedHours || 'N/A'}</div>
                      <div className="text-gray-500">Actual: {assignment.actualHours}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {assignment.ServicePlan.basePrice ? 
                        formatCurrency(assignment.ServicePlan.basePrice, engagement?.currency) : 
                        'N/A'
                      }
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        assignment.status === 'ACTIVE' ? 'bg-green-100 text-green-800' : 
                        assignment.status === 'COMPLETED' ? 'bg-blue-100 text-blue-800' :
                        assignment.status === 'ON_HOLD' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {assignment.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="text-center py-12">
          <ChartBarIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No plans assigned</h3>
          <p className="mt-1 text-sm text-gray-500">
            Engagement plans will appear here once they are assigned.
          </p>
        </div>
      )}
    </div>
  );

  const renderCloudProviderDetails = () => (
    <div className="space-y-6">
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Cloud Provider Configuration</h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Configuration details for {engagement?.cloudProvider} provider
          </p>
        </div>
        <div className="border-t border-gray-200">
          {engagement?.cloudProvider === 'Azure' && (
            <div className="bg-blue-50 p-6">
              <h4 className="text-lg font-medium text-gray-900 mb-4">Azure Configuration</h4>
              <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Tenant ID</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {(engagement as any)?.azureDetails?.tenantIdSet ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Configured</span>
                    ) : (
                      <span className="text-gray-400">Not configured</span>
                    )}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Client ID</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {(engagement as any)?.azureDetails?.clientIdSet ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Configured</span>
                    ) : (
                      <span className="text-gray-400">Not configured</span>
                    )}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Client Secret</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {(engagement as any)?.azureDetails?.clientSecretSet ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Configured</span>
                    ) : (
                      <span className="text-gray-400">Not configured</span>
                    )}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Subscription ID</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {(engagement as any)?.azureDetails?.subscriptionIdSet ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Configured</span>
                    ) : (
                      <span className="text-gray-400">Not configured</span>
                    )}
                  </dd>
                </div>
                <div className="sm:col-span-2">
                  <dt className="text-sm font-medium text-gray-500">Authority URL</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {(engagement as any)?.azureDetails?.authorityUrlSet ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Configured</span>
                    ) : (
                      <span className="text-gray-400">Not configured</span>
                    )}
                  </dd>
                </div>
              </dl>
            </div>
          )}

          {engagement?.cloudProvider === 'AWS' && (
            <div className="bg-orange-50 p-6">
              <h4 className="text-lg font-medium text-gray-900 mb-4">AWS Configuration</h4>
              <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Account ID</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {(engagement as any)?.awsDetails?.accountIdSet ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Configured</span>
                    ) : (
                      <span className="text-gray-400">Not configured</span>
                    )}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Region</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {(engagement as any)?.awsDetails?.allRegionsSet ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        All supported regions
                      </span>
                    ) : (engagement as any)?.awsDetails?.regionSet ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Configured</span>
                    ) : (
                      <span className="text-gray-400">Not configured</span>
                    )}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Access Key ID</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {(engagement as any)?.awsDetails?.accessKeyIdSet ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Configured</span>
                    ) : (
                      <span className="text-gray-400">Not configured</span>
                    )}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Access Secret Key</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {(engagement as any)?.awsDetails?.secretAccessKeySet ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Configured</span>
                    ) : (
                      <span className="text-gray-400">Not configured</span>
                    )}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">VPC ID</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {(engagement as any)?.awsDetails?.vpcIdSet ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Configured</span>
                    ) : (
                      <span className="text-gray-400">Not configured</span>
                    )}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">IAM Role</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {(engagement as any)?.awsDetails?.iamRoleSet ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Configured</span>
                    ) : (
                      <span className="text-gray-400">Not configured</span>
                    )}
                  </dd>
                </div>
              </dl>
            </div>
          )}

          {engagement?.cloudProvider === 'GCP' && (
            <div className="bg-green-50 p-6">
              <h4 className="text-lg font-medium text-gray-900 mb-4">GCP Configuration</h4>
              <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Project ID</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {(engagement as any)?.gcpDetails?.projectIdSet ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Configured</span>
                    ) : (
                      <span className="text-gray-400">Not configured</span>
                    )}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Region</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {(engagement as any)?.gcpDetails?.allRegionsSet ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        All supported regions
                      </span>
                    ) : (engagement as any)?.gcpDetails?.regionSet ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Configured</span>
                    ) : (
                      <span className="text-gray-400">Not configured</span>
                    )}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Service account key</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {(engagement as any)?.gcpDetails?.serviceAccountSet ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Configured</span>
                    ) : (
                      <span className="text-gray-400">Not configured</span>
                    )}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Zone</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {(engagement as any)?.gcpDetails?.zoneSet ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Configured</span>
                    ) : (
                      <span className="text-gray-400">Not configured</span>
                    )}
                  </dd>
                </div>
              </dl>
            </div>
          )}

          {!engagement?.cloudProvider && (
            <div className="text-center py-12">
              <CloudIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No cloud provider configured</h3>
              <p className="mt-1 text-sm text-gray-500">
                Cloud provider configuration will appear here once it's set up.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderResources = () => {
    // Filter resources by search term
    const filteredResources = resources.filter((resource: any) => {
      if (!resourceSearchTerm.trim()) return true;
      const searchLower = resourceSearchTerm.toLowerCase();
      return (
        resource.name?.toLowerCase().includes(searchLower) ||
        resource.resourceType?.toLowerCase().includes(searchLower) ||
        resource.resourceGroupName?.toLowerCase().includes(searchLower)
      );
    });

    return (
    <div className="space-y-6">
      {/* Header with Sync Button */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg leading-6 font-medium text-gray-900">Cloud Resources</h3>
          <p className="mt-1 text-sm text-gray-600">
            Resources synced from the cloud provider for this engagement
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => setShowResourceSyncModal(true)}
            disabled={resourcesLoading}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <CloudIcon className="-ml-1 mr-2 h-4 w-4" />
            Sync from Cloud
          </button>
          <Link
            to="/resources"
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <ServerIcon className="-ml-1 mr-2 h-4 w-4" />
            View All Resources
          </Link>
        </div>
      </div>

      {/* Search Input */}
      {resources.length > 0 && (
        <div className="flex items-center gap-3">
          <div className="relative flex-1">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
            </div>
            <input
              type="text"
              value={resourceSearchTerm}
              onChange={(e) => setResourceSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
              placeholder="Search resources by name, type, or resource group..."
              className="block w-full rounded-md border border-gray-300 bg-white py-2 pl-10 pr-10 text-sm placeholder-gray-500 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            {resourceSearchTerm && (
              <button
                onClick={() => setResourceSearchTerm('')}
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
              >
                <span className="sr-only">Clear search</span>
                <span className="text-lg">&times;</span>
              </button>
            )}
          </div>
          <button
            type="button"
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <MagnifyingGlassIcon className="-ml-1 mr-2 h-4 w-4" />
            Search
          </button>
          {resourceSearchTerm && (
            <span className="text-sm text-gray-600 whitespace-nowrap">
              <span className="font-semibold text-blue-600">{filteredResources.length}</span> of {resources.length} resources match
            </span>
          )}
        </div>
      )}

      {/* Show loading state */}
      {resourcesLoading ? (
        <div className="flex justify-center items-center py-12 bg-white rounded-lg shadow">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : /* Resources List */
      resources && resources.length > 0 ? (
        filteredResources.length > 0 ? (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {filteredResources.map((resource: any) => (
              <li key={resource.id} className={resource.status === 'Deleted' ? 'bg-red-50' : ''}>
                <div className="px-4 py-4 flex items-center justify-between">
                  <div className="flex items-center flex-1">
                    {resource.status === 'Deleted' ? (
                      <ExclamationTriangleIcon className="flex-shrink-0 h-5 w-5 text-red-500" />
                    ) : (
                      <ServerIcon className="flex-shrink-0 h-5 w-5 text-gray-400" />
                    )}
                    <div className="ml-4 flex-1">
                      <div className="flex items-center">
                        <p className={`text-sm font-medium truncate ${resource.status === 'Deleted' ? 'text-gray-600 line-through' : 'text-gray-900'}`}>
                          {resource.name}
                        </p>
                        <span className={`ml-2 inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                          resource.status === 'Active'
                            ? 'bg-green-100 text-green-800'
                            : resource.status === 'Inactive'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {resource.status}
                        </span>
                      </div>
                      <p className={`text-sm ${resource.status === 'Deleted' ? 'text-gray-500' : 'text-gray-500'}`}>
                        {resource.resourceType} • {resource.resourceGroupName}
                        {resource.region && ` • ${resource.region}`}
                      </p>
                      <div className="flex items-center space-x-3 mt-1 flex-wrap">
                        {/* Sync Recency Indicator */}
                        {(() => {
                          const syncStatus = getSyncRecencyStatus(resource.syncedAt);
                          return (
                            <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${
                              syncStatus.status === 'recent'
                                ? 'bg-green-100 text-green-800'
                                : syncStatus.status === 'earlier'
                                ? syncStatus.color === 'yellow'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : syncStatus.color === 'orange'
                                  ? 'bg-orange-100 text-orange-800'
                                  : 'bg-red-100 text-red-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {syncStatus.label}
                            </span>
                          );
                        })()}
                        {resource.syncedAt && (
                          <p className="text-xs text-gray-400">
                            Last synced: {formatDateTimeShort(resource.syncedAt)}
                          </p>
                        )}
                        {resource.deletedAt && (
                          <p className="text-xs text-red-600 font-medium">
                            Deleted from cloud: {formatDateTimeShort(resource.deletedAt)}
                          </p>
                        )}
                        {resource.lastReconciledAt && (
                          <p className="text-xs text-gray-400">
                            Verified: {formatDateTimeShort(resource.lastReconciledAt)}
                          </p>
                        )}
                      </div>
                      {resource.status === 'Deleted' && (
                        <div className="mt-2 flex items-start">
                          <ExclamationTriangleIcon className="h-4 w-4 text-orange-500 mr-1 mt-0.5 flex-shrink-0" />
                          <p className="text-xs text-orange-700">
                            This resource was detected as deleted from the cloud provider during sync reconciliation. Related Alerts and Cases have been automatically closed.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {resource.cloudProvider && (
                      <div className="flex items-center text-xs text-gray-500">
                        <CloudIcon className="h-3 w-3 mr-1" />
                        {resource.cloudProvider}
                      </div>
                    )}
                    <button
                      onClick={() => {
                        setSelectedResourceId(resource.id);
                        setShowResourceDetailModal(true);
                      }}
                      className="text-indigo-600 hover:text-indigo-900 text-sm font-medium"
                    >
                      View
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
        ) : (
          /* No search results message */
          <div className="text-center py-12 bg-white rounded-lg shadow">
            <MagnifyingGlassIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No resources match your search</h3>
            <p className="mt-1 text-sm text-gray-500">
              Try adjusting your search term to find what you're looking for.
            </p>
            <div className="mt-4">
              <button
                onClick={() => setResourceSearchTerm('')}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Clear search
              </button>
            </div>
          </div>
        )
      ) : (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <ServerIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No resources found</h3>
          <p className="mt-1 text-sm text-gray-500">
            Sync resources from your cloud provider to get started.
          </p>
          <div className="mt-6">
            <button
              onClick={() => setShowResourceSyncModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <CloudIcon className="-ml-1 mr-2 h-5 w-5" />
              Sync from Cloud
            </button>
          </div>
        </div>
      )}
    </div>
  );
  };

  const renderBilling = () => (
    <div className="space-y-6">
      {engagement?.patchingServices && engagement.patchingServices.length > 0 && (
        <PatchingServicesDetail
          patchingServices={engagement.patchingServices}
          engagementStartDate={engagement.startDate}
          currency={engagement.currency}
        />
      )}
      {billingSummary && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Total Budget
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {formatCurrency(billingSummary.engagement.totalBudget, billingSummary.engagement.currency)}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Actual Cost
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {formatCurrency(billingSummary.summary.totalActualCost, billingSummary.engagement.currency)}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Projected Cost
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {formatCurrency(billingSummary.summary.totalProjectedCost, billingSummary.engagement.currency)}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Budget Utilization
                      </dt>
                      <dd className="flex items-baseline">
                        <div className="text-lg font-medium text-gray-900">
                          {billingSummary.summary.budgetUtilization.toFixed(1)}%
                        </div>
                        {billingSummary.summary.budgetUtilization > 80 && (
                          <ExclamationTriangleIcon className="ml-2 h-5 w-5 text-yellow-500" />
                        )}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Agent Billing Details */}
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">Agent Billing Details</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Agent
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rate
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Hours
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Projected Cost
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actual Cost
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {billingSummary.agents.map((agent) => (
                    <tr key={agent.agentId}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {agent.agentName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {agent.role.replace('_', ' ')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(agent.hourlyRate, billingSummary.engagement.currency)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div>Allocated: {agent.allocatedHours}</div>
                        <div className="text-gray-500">Utilized: {agent.utilizedHours}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(agent.projectedCost, billingSummary.engagement.currency)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(agent.actualCost, billingSummary.engagement.currency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );

  const renderWorkNotes = () => (
    <div className="space-y-6">
      {engagement?.EngagementWorkNote && engagement.EngagementWorkNote.length > 0 ? (
        <div className="space-y-4">
          {engagement.EngagementWorkNote.map((note) => (
            <div key={note.id} className="bg-white shadow rounded-lg p-6">
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <div className="h-8 w-8 rounded-full bg-gray-300 flex items-center justify-center">
                      <span className="text-sm font-medium text-gray-700">
                        {note.User.firstName[0]}{note.User.lastName[0]}
                      </span>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {note.User.firstName} {note.User.lastName}
                    </p>
                    <p className="text-sm text-gray-500">{formatDateTimeShort(note.timestamp)}</p>
                  </div>
                </div>
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                  note.isPublic ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                }`}>
                  {note.isPublic ? 'Public' : 'Private'}
                </span>
              </div>
              <div className="mt-4">
                <p className="text-sm text-gray-900">{note.note}</p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No work notes</h3>
          <p className="mt-1 text-sm text-gray-500">
            Work notes and updates will appear here.
          </p>
        </div>
      )}
    </div>
  );

  // Show loading while permissions are being fetched
  if (permissionsLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!canView) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          You don't have permission to access this page.
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !engagement) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md">
          {error || 'Engagement not found'}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center mb-4">
          <Link
            to="/engagements"
            className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700"
          >
            <ArrowLeftIcon className="mr-2 h-4 w-4" />
            Back to Engagements
          </Link>
        </div>
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-semibold text-gray-900">{engagement.name}</h1>
              {engagement.isServiceCreditEngagement && (
                <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-1 text-xs font-semibold text-blue-800">
                  Service Credit Engagement
                </span>
              )}
            </div>
            <p className="mt-1 text-sm text-gray-600">
              {engagement.number} • {engagement.Customer?.accountName || 'N/A'}
            </p>
          </div>
          <div className="flex space-x-3">
            <PermissionGate resource="engagements" action="update">
              <Link
                to={`/engagements/${engagement.id}/edit`}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <PencilIcon className="-ml-1 mr-2 h-4 w-4" />
                Edit
              </Link>
            </PermissionGate>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center`}
            >
              <tab.icon className="mr-2 h-4 w-4" />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div>
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'agents' && renderAgents()}
        {activeTab === 'plans' && renderPlans()}
        {activeTab === 'sla' && engagement && <SlaPolicies engagementId={engagement.id} />}
        {activeTab === 'serviceproviders' && engagement && (
          <ServiceProvidersTab
            engagementId={engagement.id}
            engagementName={engagement.name}
            onRefreshEngagement={fetchEngagement}
          />
        )}
        {activeTab === 'cloudprovider' && renderCloudProviderDetails()}
        {activeTab === 'billing' && renderBilling()}
        {activeTab === 'notes' && renderWorkNotes()}
        {activeTab === 'misc' && engagement?.Customer && (
          <EngagementMiscTab
            customerId={engagement.Customer.id}
            engagementId={engagement.id}
            customerName={engagement.Customer.accountName}
            engagementNumber={engagement.number}
            engagementName={engagement.name}
          />
        )}
      </div>

      {/* Resource Sync Modal */}
      {engagement && (
        <ResourceSyncModal
          isOpen={showResourceSyncModal}
          onClose={() => setShowResourceSyncModal(false)}
          engagementId={engagement.id}
          engagementName={engagement.name}
          onResourcesSynced={(_syncedResources) => {
            // Only fetch if the resources tab is active
            if (activeTab === 'resources') {
              fetchResources(); // Refresh resources after sync
            }
            setShowResourceSyncModal(false);
          }}
        />
      )}

      {/* Resource Detail Modal */}
      {selectedResourceId && (
        <ResourceDetailModal
          isOpen={showResourceDetailModal}
          onClose={() => {
            setShowResourceDetailModal(false);
            setSelectedResourceId(null);
          }}
          resourceId={selectedResourceId}
        />
      )}
    </div>
  );
};

export default EngagementDetail;